import { configToQs } from '~/helpers/configToQs'

import { availableAllowedSeoTags, tagPriorities } from '~/types/seoList'

import type Tag from '~/entities/tag'
import type TagParent from '~/entities/tagParent'
import type TagType from '~/entities/tagType'
import type { CoreFetch } from '~/types/CoreFetch'
import type TagTypeKey from '~/types/tagTypeKey'
import type { LocationQuery } from 'vue-router'

/**
 * .
 * Will make sure the provided URL parameters are to the correct length.
 *
 * @param parameterSplit - The return of split("/") of the Nuxt Context Param option.
 * @returns True if correct.
 */
export function validateParameterLength(
  parameterSplit: string[],
): parameterSplit is [string] | [string, string] {
  return parameterSplit.length >= 1 && parameterSplit.length <= 2
}

export function isTagParent(arg: Tag | TagParent): arg is TagParent {
  return arg.className === 'TagParent'
}

export function tagTypesAreAllowed(
  typeArray: (TagType | undefined)[],
): typeArray is TagType[] {
  return typeArray.every((tagType) => {
    return (
      tagType !== undefined && availableAllowedSeoTags.includes(tagType.name)
    )
  })
}

export function checkTypesPairing(tagArgs: (Tag | TagParent)[]): boolean {
  return (
    // If only one member
    tagArgs.length === 1 ||
    // Or if tags are not dupes
    (tagArgs[0].id !== tagArgs[1].id &&
      // And a different type from each other
      tagArgs[0].type !== tagArgs[1].type)
  )
}

export function checkTagArgs(
  tagArgs: (Tag | TagParent | undefined)[],
): tagArgs is (Tag | TagParent)[] {
  return tagArgs.every((e) => e !== undefined)
}

export function checkTagOrders(
  typeArray: TagType[],
  tagArgs: (Tag | TagParent)[],
): string | undefined {
  const priorities = [...tagPriorities]

  // Since checkTypesPairing was called beforehand we know that ParentTag can't be on last position position
  if (isTagParent(tagArgs[0])) return
  // Return no redirection data, since it's valid
  // Types have been validated by tagTypesAreAllowed() previously so getting a -1 return here should not happen
  const translatedScoreArray = typeArray.map((tagType) =>
    priorities.indexOf(tagType.name),
  )

  // If computed translated score sustraction is not negative
  // or last member is a TagParent a.k.a. "genre" in our case
  // then priorities where not respected and needs to be swapped
  if (
    translatedScoreArray[0] - translatedScoreArray[1] >= 0 ||
    (tagArgs[1] && isTagParent(tagArgs[1]))
  ) {
    return `${tagArgs
      .reverse()
      .map((e) => e.name)
      .join('/')}/`
  } else {
    return undefined
  }
}

export function getNeedToFetchInfluencerList(
  coreFetch: CoreFetch,
  tagArgs: (Tag | TagParent)[],
  typeArray: TagType[],
): Promise<number[]> {
  // Create query for influencer filter
  const sendData = tagArgs.reduce(
    (accumulator, element, index) => {
      const typeName = typeArray[index].name

      if (element.className !== 'TagParent') {
        if (!accumulator[typeName]) accumulator[typeName] = `${element.id}`
        else accumulator[typeName] += `,${element.id}`
      } else if (!accumulator.parents) {
        accumulator.parents = `${element.id}`
      } else {
        accumulator.parents += `,${element.id}`
      }

      return accumulator
    },
    {} as Record<TagTypeKey | 'parents', string>,
  )

  return coreFetch.$get<number[]>(
    `/influencer/query/?${configToQs({ ...sendData, seo: 'True' })}`,
  )
}

export function getPageStartIndex(
  query: LocationQuery,
  setLength: number,
  limit: number,
): { currentPage: number } {
  const page = query.page
  const pageNumber = Number(page)
  const maxPageCount = Math.ceil(setLength / limit)
  let currentPage: number

  if (Number.isNaN(pageNumber)) {
    currentPage = 0
  } else {
    currentPage =
      pageNumber < 0
        ? 0
        : pageNumber >= maxPageCount
          ? maxPageCount - 1
          : pageNumber
  }

  return { currentPage }
}
